import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx'; // <--- MUST IMPORT APP, NOT COMMAND CENTER
import './index.css';
import { registerSW } from 'virtual:pwa-register';
import { notifyUpdateAvailable, isUpdateAvailable, triggerUpdate } from './utils/updateManager';
import { isDisplayMode, createDisplayUpdater, DISPLAY_TICK_MS } from './utils/displayMode';
import { installStaleChunkRecovery } from './utils/staleChunk';

// ─────────────────────────────────────────────────────────────────────────────
// BUILD-2026.306 / RMI-2026-101 — RELIABLE AUTO-UPDATE OF THE DEPLOYED BUILD
//
// PROBLEM: clients kept running a stale cached JS bundle. A new deploy would not
// load until the user manually cleared cache + unregistered the service worker.
//
// ROOT CAUSE: three pieces of code fought over the SW lifecycle —
//   1. vite-plugin-pwa (registerType:'autoUpdate') auto-registers the SW
//   2. the old RMI-090 hack here unconditionally unregistered ALL SWs on every
//      load — but unregister() does NOT clear the workbox precache in Cache
//      Storage, so stale bundles survived and got re-served
//   3. usePushNotifications.js registers /sw.js for push
// autoUpdate's weak link is DETECTION: the browser only re-checks sw.js on
// navigation, so a tab left open all day never notices a new deploy.
//
// FIX: let vite-plugin-pwa own registration via registerSW (below), and make
// update detection ACTIVE rather than navigation-only:
//   - poll registration.update() every 60s (only while online + visible)
//   - check for an update whenever the tab regains visibility
//   - when a new SW takes control, reload ONCE (guarded against reload loops)
// cleanupOutdatedCaches:true (in vite.config.js) purges old precaches on
// activation so stale bundles can't linger.
//
// The old unconditional-unregister block is intentionally GONE. Removing it is
// what lets the precache + update flow behave consistently.
// ─────────────────────────────────────────────────────────────────────────────

const SW_UPDATE_POLL_MS = 60 * 1000; // check for a new deployed build every 60s

if ('serviceWorker' in navigator) {
  // Did this page load already have a controlling SW? If so, a later
  // controllerchange means a NEW SW took over → reload to pick up the new
  // bundle. If there was NO controller at load (brand-new visitor, first SW
  // claiming the page via clientsClaim), we must NOT reload — that first claim
  // is expected and reloading there would be a spurious refresh.
  const hadControllerAtLoad = !!navigator.serviceWorker.controller;
  let refreshing = false;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;            // guard: only reload once
    if (!hadControllerAtLoad) return;  // guard: skip the initial clientsClaim
    refreshing = true;
    window.location.reload();
  });

  const updateSW = registerSW({
    immediate: true,
    onRegisteredSW(swUrl, registration) {
      if (!registration) return;
      // Active polling so an already-open tab notices a new deploy without
      // needing a manual reload. Only poll when the tab is visible and online
      // to avoid needless network chatter on backgrounded/offline tabs.
      setInterval(() => {
        if (document.visibilityState !== 'visible') return;
        if (!navigator.onLine) return;
        registration.update().catch(() => {});
      }, SW_UPDATE_POLL_MS);

      // Also check the moment the tab regains focus — catches the common case
      // of a tab backgrounded across a deploy then refocused.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && navigator.onLine) {
          registration.update().catch(() => {});
        }
      });
    },
    onNeedRefresh() {
      // Instead of auto-reloading (which interrupts work), notify the ticker
      // strip so the user can click to update at a safe moment.
      notifyUpdateAvailable(() => updateSW(true));
    },
    onRegisterError(err) {
      console.error('[PWA] Service worker registration failed:', err);
    },
  });
}

// RMI-2026-713 -- STALE BUILD RECOVERY: a page opened before a deploy asks for old hashed lazy-loaded files that no longer exist (everything beyond the
// login screen is React.lazy) and would sit on its loader forever. Vite reports that as 'vite:preloadError'; reload once (60 s cooldown, no loops).
installStaleChunkRecovery(window, (() => { try { return window.sessionStorage; } catch { return undefined; } })(), () => window.location.reload());

// RMI-2026-710 -- DISPLAY MODE: an unattended wall screen (opened once with ?display=1, remembered in localStorage) applies a waiting update by
// itself instead of waiting for someone to click UPDATE. It only does so when the screen is idle (no keyboard/mouse/touch for 60 s) and at most once
// per 5 minutes, so it cannot interrupt a person or reload-loop. Normal users are untouched: display mode is off unless asked for.
const displayStorage = (() => { try { return window.localStorage; } catch { return undefined; } })();
if (isDisplayMode(window.location.search, displayStorage)) {
  const displayUpdater = createDisplayUpdater({ trigger: triggerUpdate, storage: displayStorage });
  ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel'].forEach((ev) => window.addEventListener(ev, displayUpdater.noteInput, { passive: true }));
  setInterval(() => displayUpdater.tick(isUpdateAvailable()), DISPLAY_TICK_MS);
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />  {/* <--- THIS MUST BE <App /> TO ENABLE AUTH */}
  </React.StrictMode>,
);
